import React, { useState, useEffect, useMemo } from 'react';
import { Calendar, Search, GraduationCap } from 'lucide-react';
import Select from 'react-select';
import { ApplicationFormData, ApplicationFormErrors } from '@/types/application';
import { AgentCourse } from '@/services/agentApi';
import { agentApi } from '@/services/agentApi';

interface QualificationStepProps {
  selectedCourse?: AgentCourse | null;
  formData: ApplicationFormData;
  errors: ApplicationFormErrors;
  updateFormData: (updates: Partial<ApplicationFormData>) => void;
  setErrors: (errors: ApplicationFormErrors) => void;
}

export const QualificationStep: React.FC<QualificationStepProps> = ({
  formData,
  selectedCourse,
  errors,
  updateFormData,
  setErrors,
}) => {
  const [programs, setPrograms] = useState<any[]>([]);
  const [loadingPrograms, setLoadingPrograms] = useState(false);
  const [institutionName, setInstitutionName] = useState<string>('');

  const parseStartDates = (startDateValue?: string) => {
    if (!startDateValue) return [];
    return startDateValue
      .split(/\r?\n|,/)
      .map(date => date.trim().replace(/^[\-\u2022•]+\s*/, ''))
      .filter(Boolean);
  };

  const normalizeIntakeValue = (value?: string) => {
    if (!value) return '';
    const trimmed = value.trim();
    if (!trimmed) return '';
    const normalizedDate = new Date(trimmed);
    if (!isNaN(normalizedDate.getTime())) {
      return normalizedDate.toISOString().split('T')[0];
    }
    return trimmed.toLowerCase();
  };

  useEffect(() => {
    if (formData.universityId) {
      loadPrograms(formData.universityId);
      loadInstitutionInfo();
    } else {
      setPrograms([]);
      setInstitutionName('');
      updateFormData({ programmeStrand: '', universityProgramId: undefined, programmeStartDate: '' });
    }
  }, [formData.universityId]);

  const loadPrograms = async (universityId: number) => {
    setLoadingPrograms(true);
    try {
      const data = await agentApi.getUniversityPrograms(universityId);
      console.log('Raw API response:', data);
      console.log('Is array?', Array.isArray(data));
      console.log('Data type:', typeof data);
      
      // The API service already extracts response.data.data, so data should be an array
      const programsArray = Array.isArray(data) ? [...data] : [];
      if (selectedCourse && selectedCourse.universityId === universityId && !programsArray.some(program => program.id === selectedCourse.offeringId)) {
        programsArray.push({ id: selectedCourse.offeringId, name: selectedCourse.programmeName, level: selectedCourse.level, startDate: selectedCourse.startDate });
      }
      console.log('Programs array length:', programsArray.length);
      console.log('First program:', programsArray[0]);
      
      setPrograms(programsArray);
    } catch (error) {
      console.error('Error loading programs:', error);
      console.error('Error details:', error);
      setPrograms(selectedCourse && selectedCourse.universityId === universityId ? [{ id: selectedCourse.offeringId, name: selectedCourse.programmeName, level: selectedCourse.level, startDate: selectedCourse.startDate }] : []);
    } finally {
      setLoadingPrograms(false);
    }
  };

  const loadInstitutionInfo = async () => {
    if (!formData.universityId) return;
    
    try {
      const targetId = Number(formData.universityId);
      
      // Try direct API call first
      try {
        const university = await agentApi.getUniversityById(targetId);
        if (university && university.name) {
          setInstitutionName(university.name);
          return;
        }
      } catch (err) {
        console.warn('Error fetching university directly:', err);
      }
      
      // Fallback: try approved institutions
      try {
        const institutions = await agentApi.getApprovedInstitutions();
        const institution = institutions.find(inst => {
          const instId = Number(inst.id);
          return instId === targetId;
        });
        
        if (institution && institution.name) {
          setInstitutionName(institution.name);
          return;
        }
      } catch (err) {
        console.warn('Error loading approved institutions:', err);
      }
      
      // If all else fails, use a default
      setInstitutionName('this institution');
    } catch (error) {
      console.error('Error loading institution info:', error);
      setInstitutionName('this institution');
    }
  };

  const handleChange = (field: keyof ApplicationFormData, value: any) => {
    updateFormData({ [field]: value });
    if (errors[field]) {
      setErrors({ ...errors, [field]: undefined });
    }
  };

  const handleProgramStrandChange = (selectedOption: any) => {
    if (selectedOption) {
      const selectedProgram = programs.find(p => p.id === selectedOption.value);
      if (selectedProgram) {
        // The API returns transformed programs with name and level directly
        const programName = selectedProgram.name || '';
        updateFormData({ 
          programmeStrand: programName,
          universityProgramId: selectedProgram.id,
          programmeStartDate: '' // reset start date when program changes
        });
      }
    } else {
      updateFormData({ programmeStrand: '', universityProgramId: undefined, programmeStartDate: '' });
    }

    if (errors.programmeStrand || errors.universityProgramId) {
      setErrors({
        ...errors,
        programmeStrand: undefined,
        universityProgramId: undefined,
      });
    }
  };

  // Transform programs into react-select format
  const programOptions = useMemo(() => {
    return programs.map((prog) => {
      const programName = prog.name || '';
      const programLevel = prog.level || '';
      const label = programLevel ? `${programName} - ${programLevel}` : programName;
      return {
        value: prog.id,
        label: label,
      };
    });
  }, [programs]);

  // Find the selected option based on current formData
  const selectedOption = useMemo(() => {
    if (!formData.universityProgramId) return null;
    return programOptions.find(opt => opt.value === formData.universityProgramId) || null;
  }, [formData.universityProgramId, programOptions]);

  const selectedProgram = useMemo(
    () => programs.find(p => p.id === formData.universityProgramId),
    [programs, formData.universityProgramId]
  );

  const availableStartDates = useMemo(
    () => parseStartDates(selectedProgram?.startDate),
    [selectedProgram]
  );

  const intakeOptions = useMemo(() => {
    if (!formData.programmeStartDate) {
      return availableStartDates;
    }

    const savedIntakeNormalized = normalizeIntakeValue(formData.programmeStartDate);
    const hasMatchingOption = availableStartDates.some(
      (date) => normalizeIntakeValue(date) === savedIntakeNormalized
    );

    return hasMatchingOption
      ? availableStartDates
      : [formData.programmeStartDate, ...availableStartDates];
  }, [availableStartDates, formData.programmeStartDate]);

  const resolvedIntakeValue = useMemo(() => {
    if (!formData.programmeStartDate) return '';

    const savedIntakeNormalized = normalizeIntakeValue(formData.programmeStartDate);
    const matchingOption = intakeOptions.find(
      (date) => normalizeIntakeValue(date) === savedIntakeNormalized
    );

    return matchingOption || formData.programmeStartDate;
  }, [formData.programmeStartDate, intakeOptions]);

  useEffect(() => {
    if (
      selectedProgram &&
      availableStartDates.length > 0 &&
      formData.programmeStartDate &&
      !availableStartDates.some(
        (date) => normalizeIntakeValue(date) === normalizeIntakeValue(formData.programmeStartDate)
      )
    ) {
      // Keep existing persisted value instead of clearing it when formats differ
      // (e.g. "2025-11-01" vs "Nov 2025").
      return;
    }
  }, [selectedProgram, availableStartDates, formData.programmeStartDate, updateFormData]);

  return (
    <div>
      <h5 className="fw-bold mb-3">A. Qualification You Are Applying For</h5>
      
      {!formData.universityId && (
        <div className="alert alert-warning mb-3">
          <strong>No institution selected.</strong> Please go back to step 1 to select an institution first.
        </div>
      )}

      <div className="row">
        <div className="col-md-6 mb-3">
          <label className="form-label fw-semibold">
            Programme Strand <span className="text-danger">*</span>
          </label>
          <div className="input-group">
            <span className="input-group-text bg-light">
              <GraduationCap size={18} className="text-muted" />
            </span>
            <div style={{ flex: 1, minWidth: 0 }}>
              <Select
                value={selectedOption}
                onChange={handleProgramStrandChange}
                options={programOptions}
                isSearchable={true}
                isClearable={true}
                placeholder={
                  !formData.universityId
                    ? 'Please select an institution first'
                    : loadingPrograms
                    ? 'Loading programs...'
                    : 'Select a programme'
                }
                isDisabled={!formData.universityId || loadingPrograms}
                isLoading={loadingPrograms}
                noOptionsMessage={() => 'No programs available'}
                styles={{
                  control: (base, state) => ({
                    ...base,
                    border: errors.programmeStrand
                      ? '1px solid #dc3545'
                      : state.isFocused
                      ? '1px solid #86b7fe'
                      : '1px solid #ced4da',
                    boxShadow: errors.programmeStrand
                      ? '0 0 0 0.25rem rgba(220, 53, 69, 0.25)'
                      : state.isFocused
                      ? '0 0 0 0.25rem rgba(13, 110, 253, 0.25)'
                      : 'none',
                    borderRadius: '0.375rem',
                    minHeight: '38px',
                    '&:hover': {
                      border: errors.programmeStrand ? '1px solid #dc3545' : '1px solid #86b7fe',
                    },
                  }),
                  placeholder: (base) => ({
                    ...base,
                    color: '#6c757d',
                  }),
                  input: (base) => ({
                    ...base,
                    margin: 0,
                    padding: 0,
                  }),
                  valueContainer: (base) => ({
                    ...base,
                    padding: '0.375rem 0.75rem',
                  }),
                }}
                className="react-select-container"
                classNamePrefix="react-select"
              />
            </div>
          </div>
          {errors.programmeStrand && (
            <div className="invalid-feedback d-block">{errors.programmeStrand}</div>
          )}
          {formData.programmeStrand && (
            <small className="text-muted mt-1 d-block">
              Selected: {formData.programmeStrand}
            </small>
          )}
        </div>
      
        <div className="col-md-6 mb-3">
          <label className="form-label fw-semibold">
            Intake <span className="text-danger">*</span>
          </label>
          <div className="input-group">
            <span className="input-group-text bg-light">
              <Calendar size={18} className="text-muted" />
            </span>
            <select
              className={`form-select ${errors.programmeStartDate ? 'is-invalid' : ''}`}
              value={resolvedIntakeValue}
              onChange={(e) => handleChange('programmeStartDate', e.target.value)}
              disabled={!selectedProgram || availableStartDates.length === 0}
            >
              <option value="">
                {!selectedProgram
                  ? 'Select a programme first'
                  : availableStartDates.length === 0
                  ? 'No start dates available'
                  : 'Select an intake date'}
              </option>
              {intakeOptions.map((date) => (
                <option key={date} value={date}>
                  {date}
                </option>
              ))}
            </select>
          </div>
          {errors.programmeStartDate && (
            <div className="invalid-feedback d-block">{errors.programmeStartDate}</div>
          )}
        </div>

        {/* <div className="col-md-6 mb-3">
          <label className="form-label fw-semibold">Programme End Date</label>
          <div className="input-group">
            <span className="input-group-text bg-light">
              <Calendar size={18} className="text-muted" />
            </span>
            <input
              type="date"
              className="form-control"
              value={formData.programmeEndDate || ''}
              onChange={(e) => handleChange('programmeEndDate', e.target.value)}
            />
          </div>
        </div> */}
      </div>

      <div className="row">
        <div className="col-md-6 mb-3">
          <div className="form-check">
            <input
              className="form-check-input"
              type="checkbox"
              id="studiedAtKalandraBefore"
              checked={formData.studiedAtKalandraBefore}
              onChange={(e) => handleChange('studiedAtKalandraBefore', e.target.checked)}
            />
            <label className="form-check-label" htmlFor="studiedAtKalandraBefore">
              Have you studied at {institutionName || 'this institution'} before?
            </label>
          </div>
        </div>

        {formData.studiedAtKalandraBefore && (
          <div className="col-md-6 mb-3">
            <label className="form-label fw-semibold">When did you study at {institutionName || 'this institution'}?</label>
            <input
              type="text"
              className="form-control"
              value={formData.howDidYouFindOutWhen || ''}
              onChange={(e) => handleChange('howDidYouFindOutWhen', e.target.value)}
              placeholder="e.g. 2022, 2021–2023, Semester 1 2024"
            />
          </div>
        )}
      </div>

      <div className="row">
        <div className="col-md-6 mb-3">
          <label className="form-label fw-semibold">
            How did you find out about {institutionName || 'this institution'}? <span className="text-danger">*</span>
          </label>
          <div className="input-group">
            <span className="input-group-text bg-light">
              <Search size={18} className="text-muted" />
            </span>
            <select
              className={`form-control ${errors.howDidYouFindOut ? 'is-invalid' : ''}`}
              value={formData.howDidYouFindOut || ''}
              onChange={(e) => handleChange('howDidYouFindOut', e.target.value)}
            >
              <option value="">Select an option</option>
              <option value="Scholarvest">Scholarvest</option>
            </select>
          </div>
          {errors.howDidYouFindOut && (
            <div className="invalid-feedback d-block">{errors.howDidYouFindOut}</div>
          )}
        </div>
      </div>
    </div>
  );
};
